import ajax from "./ajax";

export function requestBlankProductsAddedByAdmin() {
  return ajax(`/blank-products`);
}

export function requestBlankProduct(id, query) {
  return ajax(`/blank-products/${id}`, { query, timeout: 45000 });
}

/** Shared Firestore docs for a mill style — used when Typesense is empty/down. */
export function lookupBlankProductsByStyle(style) {
  return ajax(`/blank-products/lookup`, {
    query: { style },
    timeout: 15000,
  });
}

export function requestRecommendedBlankProduct(id, data) {
  return ajax(`/blank-products/recommended-supplier-psds/${id}`, {
    method: "PUT",
    data,
  });
}

export function requestNewBlankProduct(supplierId, blankId) {
  // AS/ASAU page the catalog; SanMar re-hosts color images before responding.
  const sid = String(supplierId || "").toUpperCase();
  const slowCatalog = ["AS", "ASAU", "SANMAR", "SANMARCA"].includes(sid);
  return ajax(`/suppliers/products`, {
    server: "python",
    query: {
      id: supplierId,
      productId: blankId,
    },
    timeout: slowCatalog ? 180000 : 60000,
  });
}

export function addOrUpdateBlankProduct(data, id) {
  return ajax(`/blank-products/${id}`, { method: "PUT", data });
}

/** Stores this base product is live in: { channels: [{ shopId, name, listings }] }. */
export function getBlankChannels(id) {
  return ajax(`/blank-products/${id}/channels`);
}

export function updateBlankProductRecommendation(data, id) {
  return ajax(`/blank-products/${id}/recommended`, { method: "PUT", data });
}

/**
 * Deleting a base product also removes its stock rows, so the server refuses to
 * discard a non-zero on-hand quantity unless the caller acknowledges it.
 */
export function deleteBlankProduct(id, { acknowledgeStock = false } = {}) {
  return ajax(`/blank-products/${id}`, {
    method: "DELETE",
    query: acknowledgeStock ? { acknowledgeStock: "true" } : undefined,
  });
}

/**
 * Read-only preview of what deleting the given base products would affect —
 * synced listings, open orders and on-hand stock. Used to fill in the delete
 * confirmation with real numbers instead of a generic warning.
 */
export function getBlankDeleteImpact(ids) {
  return ajax(`/blank-products/delete-impact`, {
    method: "POST",
    data: { ids },
  });
}

/**
 * Read-only: listings and open order lines mapped to these blank variants,
 * so removing a size/color can warn before those listings go unsynced.
 */
export function getBlankVariantDeleteImpact(id, variantIds) {
  return ajax(`/blank-products/${id}/variant-delete-impact`, {
    method: "POST",
    data: { variantIds },
  });
}

/**
 * Replace base product: move every listing on one blank to another.
 * Candidates and preview are read-only; startReplaceBlank returns { jobId }
 * for a background job polled with getReplaceJob.
 */
export function getReplaceCandidates(id, q = "") {
  return ajax(`/blank-products/${id}/replace/candidates`, {
    query: q ? { q } : undefined,
    timeout: 60000,
  });
}

export function previewReplaceBlank(id, targetBlankId) {
  return ajax(`/blank-products/${id}/replace/preview`, {
    method: "POST",
    data: { targetBlankId },
    timeout: 60000,
  });
}

export function startReplaceBlank(id, data) {
  return ajax(`/blank-products/${id}/replace`, {
    method: "POST",
    data,
    timeout: 60000,
  });
}

export function getReplaceJob(jobId) {
  return ajax(`/blank-products/replace-jobs/${jobId}`);
}

export function retryReplaceJob(jobId) {
  return ajax(`/blank-products/replace-jobs/${jobId}/retry`, {
    method: "POST",
    data: {},
  });
}

/**
 * Deep-copy a manually created base product — sizes, colors, mockups and design
 * areas carry over; the copy needs its own name and style number.
 */
export function duplicateBlankProduct(id, { name, style }) {
  return ajax(`/blank-products/${id}/duplicate`, {
    method: "POST",
    data: { name, style },
  });
}

export function getBlankSettingsAndPricingInfo(id) {
  return ajax(`/blank-products/${id}/pricing-info`);
}

export function saveBlankProductPlacementDetails(id, data) {
  return ajax(`products/${id}/placements`, { method: "POST", data });
}

export function getEnabledBlankProducts(query) {
  return ajax(`/blank-products/enabled`, { query });
}

export function getEnabledBlankProductDetails(id) {
  return ajax(`/blank-products/enabled/${id}`);
}

export function saveNewProductPlacementDetails(data) {
  return ajax(`/products/new-with-placements`, {
    method: "POST",
    data,
    server: "python",
  });
}

export function updateProductPlacementDetails(data, id) {
  return ajax(`/products/${id}`, {
    method: "PUT",
    data,
    server: "python",
  });
}

export function updateBackgroundImage(data, id) {
  return ajax(`/blank-products/${id}/images`, { method: "PUT", data });
}

export function getSimilarBlankProducts(id, query = {}) {
  return ajax(`/blank-products/${id}/similar`, { query });
}

/**
 * Bulk-match listing/manual variants onto a wholesaler blank (SS/SanMar/AS…).
 * Matched blankProductIds carry supplierId so POs group under that wholesaler.
 */
export function matchBlankVariants(data) {
  return ajax(`/blank-products/match-variants`, {
    method: "POST",
    data,
  });
}

export function storeSimilarProducts(data) {
  return ajax(`/products/store-similar-products`, { method: "POST", data });
}

export function requestBlankProductsCsv() {
  return ajax(`/blank-products/csv-download`);
}

/** Start async bulk publish of blanks as buyer-customizable channel products. */
export function bulkSendToChannel(data) {
  return ajax(`/blank-products/bulk-send-to-channel`, {
    method: "POST",
    data,
  });
}

export function getBulkSendToChannelStatus(jobId) {
  return ajax(`/blank-products/bulk-send-to-channel/${jobId}`);
}

// Bulk "Assign print methods" runs as a background job. The tracker in the
// bottom-right corner reads these; see components/common/BulkJobs.

/** Read-only counts for the Remove confirmation. Changes nothing. */
export function preflightAssignPrintMethods(data) {
  return ajax(`/blank-products/bulk-jobs/preflight`, {
    method: "POST",
    data,
    timeout: 60000,
  });
}

export function startAssignPrintMethodsJob(data) {
  return ajax(`/blank-products/bulk-jobs`, {
    method: "POST",
    data: { type: "assign_print_methods", ...data },
  });
}

export function listActiveBulkJobs() {
  return ajax(`/blank-products/bulk-jobs`, { query: { active: "true" } });
}

export function getBulkJob(jobId) {
  return ajax(`/blank-products/bulk-jobs/${jobId}`);
}

export function getBulkJobResults(jobId, statuses = []) {
  return ajax(`/blank-products/bulk-jobs/${jobId}/results`, {
    query: statuses.length ? { status: statuses.join(",") } : undefined,
  });
}

export function cancelBulkJob(jobId) {
  return ajax(`/blank-products/bulk-jobs/${jobId}/cancel`, {
    method: "POST",
    data: {},
  });
}

export function retryFailedBulkJob(jobId) {
  return ajax(`/blank-products/bulk-jobs/${jobId}/retry-failed`, {
    method: "POST",
    data: {},
  });
}

export function dismissBulkJob(jobId) {
  return ajax(`/blank-products/bulk-jobs/${jobId}/dismiss`, {
    method: "POST",
    data: {},
  });
}

// Detect whether a Riverr "master" exists for this blank's style (opt-in
// "Update to new version" prompt in the edit UI).
export function getMasterUpdateInfo(id) {
  return ajax(`/blank-products/${id}/master-update`);
}

// Apply the master overlay (mockups + canvas/print sizes) to an existing blank.
export function applyMasterUpdate(id) {
  return ajax(`/blank-products/${id}/master-update`, {
    method: "POST",
    data: {},
  });
}

export function getBlankProductCategories(query = {}) {
  return ajax(`/blank-products/categories`, { query });
}

export function setFeaturedBlankProductCategories(categoryIds = []) {
  return ajax(`/blank-products/categories/featured`, {
    method: "PUT",
    data: { categoryIds },
  });
}

export function getBlankCategoryProducts(categoryId) {
  return ajax(`/blank-products/categories/${categoryId}/products`);
}

export function assignBlankProductsToCategories(data) {
  return ajax(`/blank-products/categories/assign`, {
    method: "POST",
    data,
  });
}

export function updateBlankCategoryImage(categoryId, data) {
  return ajax(`/blank-products/categories/${categoryId}/image`, {
    method: "PUT",
    data,
  });
}

export function setBlankCategoryCoverFromProduct(categoryId, productId) {
  return ajax(`/blank-products/categories/${categoryId}/cover-from-product`, {
    method: "PUT",
    data: { productId },
  });
}

/** Placement mockup upload: cut out, trim, upscale and center on 2000×2000.
 *  Resolves to { cutoutUrl, transparent, upscaled, originalBackground, source, warning }. */
export function preparePlacementMockupImage(data) {
  return ajax(`/blank-products/mockup-image/prepare`, {
    method: "POST",
    data,
    timeout: 240000,
  }).then((res) => res?.data);
}

/** Flatten a prepared cut-out onto a background color. Resolves to { file }. */
export function finalizePlacementMockupImage(data) {
  return ajax(`/blank-products/mockup-image/finalize`, {
    method: "POST",
    data,
    timeout: 60000,
  }).then((res) => res?.data);
}
